import { useState, useEffect, useRef, useCallback } from 'react';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { Logo } from '@/components/brand';

interface EnhancedLoadingScreenProps {
  /**
   * @deprecated The step-by-step list was removed. Accepted but ignored so
   * callers (e.g. App.tsx) don't need to change.
   */
  currentStep?: string;
  progress?: number;
  showSkeletons?: boolean;
}

export function EnhancedLoadingScreen({
  progress,
  showSkeletons = false
}: EnhancedLoadingScreenProps) {
  const [animatedProgress, setAnimatedProgress] = useState(0);

  // Optimized progress animation using requestAnimationFrame
  const rafRef = useRef<number | undefined>(undefined);

  const animateProgress = useCallback((targetProgress: number) => {
    const animate = () => {
      setAnimatedProgress((prev) => {
        const diff = targetProgress - prev;
        if (Math.abs(diff) < 0.5) {
          return targetProgress;
        }
        const newProgress = prev + diff * 0.15; // Smoother animation curve

        // Continue animation if not reached target
        if (Math.abs(targetProgress - newProgress) > 0.5) {
          rafRef.current = requestAnimationFrame(animate);
        }

        return newProgress;
      });
    };

    // Cancel any existing animation
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    // Never quite reach 100% until fully loaded when no explicit progress given
    const targetProgress = progress ?? 90;

    animateProgress(targetProgress);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [progress, animateProgress]);

  return (
    <div className="min-h-dvh bg-gradient-to-br from-primary/5 to-primary/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8">
        {/* Logo and Brand */}
        <div className="text-center space-y-2">
          <Logo className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground">Converlang</h1>
          <p className="text-muted-foreground">Language Learning</p>
        </div>

        {/* Progress Card */}
        <Card className="border-primary/20 bg-card/80 backdrop-blur-sm">
          <CardContent className="p-6 space-y-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <Progress value={animatedProgress} className="h-2" />
              <p className="text-xs text-muted-foreground text-center">
                {Math.round(animatedProgress)}% complete
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Skeleton Cards (optional) */}
        {showSkeletons && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-4">
                    <div className="h-4 bg-muted rounded mb-2" />
                    <div className="h-6 bg-muted rounded" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Simplified loading screen for quick transitions
export function SimpleLoadingScreen({
  message = "Loading..."
}: { message?: string }) {
  return (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  </div>
  );
}
